// Lightweight ResourceTiming collector that sends an aggregated PageAction to New Relic.
// Usage: import './perf/newrelic-bandwidth-beacon' in your app entry (or add script to index.html).
// Configure SAMPLE_RATE (0..1) to control how many clients report.
// Sends: totalBytes, resourceCount, top1Name/top1Bytes, top2Name/top2Bytes, top3Name/top3Bytes, pageUrl, userAgent.
// apps/website/src/pref/newrelic-bandwidth-beacon.ts
// apps/website/src/pref/newrelic-bandwidth-beacon.ts

(function () {
  const SAMPLE_RATE = 0.5;
  const TOP_N = 3;
  const MAX_NAME_LENGTH = 200;

  function shouldSample() {
    return Math.random() < SAMPLE_RATE;
  }

  function safeName(name: string) {
    if (!name) return '';
    return name.length > MAX_NAME_LENGTH ? name.slice(0, MAX_NAME_LENGTH) : name;
  }

  function isResourceTiming(entry: PerformanceEntry): entry is PerformanceResourceTiming {
    return (entry as PerformanceResourceTiming).initiatorType !== undefined;
  }

  function collectAndSend() {
    if (!('performance' in window) || typeof performance.getEntriesByType !== 'function') {
      console.debug('[NR Bandwidth] Performance API not available');
      return;
    }
    const entries = performance.getEntriesByType('resource') || [];
    if (!entries.length) {
      console.debug('[NR Bandwidth] No resource entries found');
      return;
    }

    const mapped = entries
      .filter(isResourceTiming)
      .map(e => {
        const size = (e.transferSize > 0)
          ? e.transferSize
          : (e.encodedBodySize > 0)
            ? e.encodedBodySize
            : 0;
        return {
          name: e.name,
          initiatorType: e.initiatorType,
          size,
          duration: e.duration
        };
      });

    const totalBytes = mapped.reduce((s, r) => s + r.size, 0);
    const resourceCount = mapped.length;

    const top = mapped
      .filter(r => r.size > 0)
      .sort((a, b) => b.size - a.size)
      .slice(0, TOP_N);

    const attrs: Record<string, any> = {
      pageUrl: location.href,
      pagePath: location.pathname,
      totalBytes,
      resourceCount,
      userAgent: navigator.userAgent
    };

    top.forEach((r, i) => {
      const idx = i + 1;
      attrs[`top${idx}Name`] = safeName(r.name);
      attrs[`top${idx}Bytes`] = r.size;
      attrs[`top${idx}Type`] = r.initiatorType || '';
    });

    try {
      if ((window as any).newrelic && typeof (window as any).newrelic.addPageAction === 'function') {
        (window as any).newrelic.addPageAction('bandwidth', attrs);
      } else if ((window as any).NREUM && (window as any).NREUM.addPageAction && typeof (window as any).NREUM.addPageAction === 'function') {
        (window as any).NREUM.addPageAction('bandwidth', attrs);
      }
    } catch (err) {
      console.error('bandwidth beacon error', err);
    }
  }

  function schedule() {
    try {
      if (!shouldSample()) {
        return;
      }
    } catch (e) { /* ignore */ }

    const delayMs = 3000;
    if (document.readyState === 'complete') {
      setTimeout(collectAndSend, delayMs);
    } else {
      window.addEventListener('load', function () {
        setTimeout(collectAndSend, delayMs);
      }, { once: true });
    }
  }

  schedule();
})();
